import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Icon, IconButton, Badge, Popover, PopoverTrigger, PopoverContent,
  PopoverHeader, PopoverBody, PopoverArrow, PopoverCloseButton,
  Stack, Text, HStack, useColorModeValue, Flex, Collapse, Button, Divider
} from '@chakra-ui/react';
import { FaBell, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { NOTIFICACOES_ATIVAS_GET } from '../../api';
import { UserContext } from '../../Context/UserContext';
import { SocketContext } from '../../Context/SocketContext';

const TYPE_CONFIG = {
  info:    { color: 'blue',   label: 'Informação', border: '#3182CE' },
  warning: { color: 'orange', label: 'Atenção',    border: '#DD6B20' },
  error:   { color: 'red',    label: 'Urgente',    border: '#E53E3E' },
  success: { color: 'green',  label: 'Novidade',   border: '#38A169' },
};

const URL_REGEX = /(https?:\/\/[^\s]+)/g;

function renderTextComLinks(texto) {
  if (!texto) return null;
  const partes = texto.split(URL_REGEX);
  return partes.map((parte, i) => {
    if (URL_REGEX.test(parte)) {
      URL_REGEX.lastIndex = 0; // reset do regex stateful
      return (
        <a
          key={i}
          href={parte}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#3182CE', textDecoration: 'underline', wordBreak: 'break-all' }}
        >
          {parte}
        </a>
      );
    }
    return parte;
  });
}

function NotificacaoCard({ n, borderColor, mutedText, itemBg }) {
  const [expandido, setExpandido] = useState(false);
  const cfg = TYPE_CONFIG[n.tipo] || TYPE_CONFIG.info;

  return (
    <Box
      p={4}
      borderRadius="xl"
      border="1px solid"
      borderColor={borderColor}
      borderLeft="4px solid"
      borderLeftColor={cfg.border}
      bg={itemBg}
      transition="all 0.2s"
    >
      <HStack justify="space-between" mb={1}>
        <Text fontWeight="800" fontSize="sm" noOfLines={expandido ? undefined : 1} flex="1">
          {n.titulo}
        </Text>
        <Badge colorScheme={cfg.color} borderRadius="full" fontSize="9px" flexShrink={0}>
          {cfg.label}
        </Badge>
      </HStack>

      <Collapse in={expandido} startingHeight="40px" animateOpacity>
        <Text fontSize="sm" color={mutedText} whiteSpace="pre-wrap">
          {renderTextComLinks(n.mensagem)}
        </Text>
      </Collapse>

      <Flex justify="space-between" align="center" mt={2}>
        <Text fontSize="10px" color="gray.400">
          Válido até: {new Date(n.data_fim).toLocaleString('pt-BR')}
        </Text>
        <Button
          size="xs"
          variant="ghost"
          colorScheme={cfg.color}
          rightIcon={expandido ? <FaChevronUp /> : <FaChevronDown />}
          onClick={() => setExpandido(!expandido)}
          h="auto"
          px={2}
          py={1}
          fontSize="10px"
        >
          {expandido ? 'Menos' : 'Ver mais'}
        </Button>
      </Flex>
    </Box>
  );
}

export default function NotificacoesBanner() {
  const { login } = useContext(UserContext);
  const socket = useContext(SocketContext);
  const [notificacoes, setNotificacoes] = useState([]);

  const cardBg = useColorModeValue('white', '#1A202C');
  const borderColor = useColorModeValue('gray.100', 'whiteAlpha.100');
  const mutedText = useColorModeValue('gray.500', 'gray.400');
  const itemBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const dividerColor = useColorModeValue('gray.200', 'whiteAlpha.300');

  const fetchAtivas = useCallback(async () => {
    if (!login) return;
    try {
      const config = NOTIFICACOES_ATIVAS_GET();
      const res = await axios(config);
      setNotificacoes(res.data.Data || []);
    } catch (err) {
      // silencia erros para não perturbar a UX
    }
  }, [login]);

  useEffect(() => {
    fetchAtivas();

    if (socket) {
      const handleUpdate = (event) => {
        console.log('Received WebSocket update in NotificacoesBanner:', event);
        fetchAtivas();
      };

      socket.on('notificacao_atualizada', handleUpdate);

      return () => {
        socket.off('notificacao_atualizada', handleUpdate);
      };
    }
  }, [socket, fetchAtivas]);

  if (!login) return null;

  return (
    <Popover placement="bottom-end" closeOnBlur>
      <PopoverTrigger>
        <Box position="relative" display="inline-block">
          <IconButton
            icon={<FaBell />}
            variant="ghost"
            borderRadius="full"
            size="md"
            aria-label="Notificações"
            color={notificacoes.length > 0 ? 'orange.400' : 'gray.400'}
          />
          {notificacoes.length > 0 && (
            <Badge
              position="absolute"
              top="2px"
              right="2px"
              borderRadius="full"
              colorScheme="red"
              fontSize="9px"
              minW="16px"
              h="16px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              pointerEvents="none"
            >
              {notificacoes.length}
            </Badge>
          )}
        </Box>
      </PopoverTrigger>
      <PopoverContent
        bg={cardBg}
        border="1px solid"
        borderColor={borderColor}
        borderRadius="2xl"
        shadow="2xl"
        w={{ base: '90vw', md: '400px' }}
        maxH="75vh"
        overflowY="auto"
      >
        <PopoverArrow bg={cardBg} />
        <PopoverCloseButton mt={1} />
        <PopoverHeader borderBottom="1px solid" borderColor={borderColor} px={5} py={4}>
          <HStack>
            <Icon as={FaBell} color="orange.400" />
            <Text fontWeight="800" fontSize="sm">Notificações do Sistema</Text>
            {notificacoes.length > 0 && (
              <Badge colorScheme="red" borderRadius="full">{notificacoes.length}</Badge>
            )}
          </HStack>
        </PopoverHeader>
        <PopoverBody px={4} py={4}>
          {notificacoes.length === 0 ? (
            <Flex justify="center" align="center" py={8} direction="column" gap={2}>
              <Icon as={FaBell} color="gray.300" fontSize="3xl" />
              <Text color={mutedText} fontSize="sm">Nenhuma notificação ativa</Text>
            </Flex>
          ) : (
            <Stack spacing={3}>
              {/* Notificações Normais (Criadas por usuários) */}
              {notificacoes.filter(n => n.criado_por !== 'sistema').map((n) => (
                <NotificacaoCard
                  key={n.id}
                  n={n}
                  borderColor={borderColor}
                  mutedText={mutedText}
                  itemBg={itemBg}
                />
              ))}

              {/* Divisor físico e legenda para alertas de saúde do sistema */}
              {notificacoes.some(n => n.criado_por === 'sistema') && (
                <>
                  {notificacoes.some(n => n.criado_por !== 'sistema') ? (
                    <Flex align="center" py={1} gap={2}>
                      <Divider borderColor={dividerColor} />
                      <Text fontSize="10px" fontWeight="800" color="gray.400" whiteSpace="nowrap" letterSpacing="wider" textTransform="uppercase">
                        Alertas de Saúde
                      </Text>
                      <Divider borderColor={dividerColor} />
                    </Flex>
                  ) : (
                    <Text fontSize="10px" fontWeight="800" color="gray.400" mb={1} letterSpacing="wider" textTransform="uppercase">
                      Alertas de Saúde do Sistema
                    </Text>
                  )}
                </>
              )}

              {/* Notificações Automatizadas (Sistema) */}
              {notificacoes.filter(n => n.criado_por === 'sistema').map((n) => (
                <NotificacaoCard
                  key={n.id}
                  n={n}
                  borderColor={borderColor}
                  mutedText={mutedText}
                  itemBg={itemBg}
                />
              ))}
            </Stack>
          )}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}
